import { tv } from 'tailwind-variants';
import {
  type ParsedSegment,
  TOTAL_HOURS,
  parseTimeSlot,
} from '@/components/authorable/shared/PricingChart/PricingBarChart.styles';

export const pieChartVariants = tv({
  slots: {
    wrapper: ['w-full', 'flex', 'flex-col', 'items-center'],
    chartArea: ['relative', 'w-full', 'aspect-[540/520] xm:aspect-[720/344]'],
    centerText: [
      'w-full',
      'h-full',
      'flex',
      'items-center',
      'justify-center',
      'text-center',
      'text-[22px]',
      'font-bold',
      'leading-[26px]',
      'text-textPrimary',
      'px-4',
    ],
    amPmLabel: [
      'flex',
      'items-center',
      'gap-2',
      'text-2xl md:text-xl',
      'font-medium',
      'text-textPrimary',
      'whitespace-nowrap',
    ],
  },
});

export const pieChartConfig = {
  viewBox: '0 0 540 520',
  center: { cx: 270, cy: 260 },
  anglePerHour: 15,
  amPmAngles: {
    am: 45,
    pm: 240,
  },
  radii: {
    ringOuter: 220,
    ringInner: 188,
    tickOuter: 180,
    tickInner: 165,
    label: 145,
    centerText: 140,
    amPmLabel: 260,
  },
  rechartsRadius: {
    outer: '87%',
    inner: '72.3%',
  },
  colors: {
    tick: '#9CA3AF',
    hourLabel: '#212529',
    sun: '#F1B72A',
    moon: '#1E2761',
    gapFill: 'var(--color-secondary)',
  },
  typography: {
    centerText: {
      fontSize: '22px',
    },
    hourLabel: {
      fontSize: 22,
      fontWeight: 500,
    },
  },
  amPmBox: {
    width: 120,
    height: 38,
  },
};

export const pieChartLegendVariants = tv({
  slots: {
    legendList: ['flex flex-col', 'gap-y-4', 'mt-4'],
    legendItem: ['relative', 'flex items-center', 'gap-4'],
    legendDot: ['shrink-0', 'w-6', 'h-6', 'rounded-full'],
    legendContent: ['[&_p]:mb-0 [&_p]:text-base [&_p]:leading-6'],
    disclaimerLabel: ['w-full', 'text-sm leading-[20px] text-textPrimary font-medium'],
    disclaimerIcon: ['fa-regular', 'fa-circle-question', 'text-secondary'],
  },
});

const formatHour = (h: number): string => {
  if (h === 0 || h === 24) return '12 a.m.';
  if (h === 12) return '12 p.m.';
  if (h < 12) return `${h} a.m.`;
  return `${h - 12} p.m.`;
};

export const formatTimeRange = (
  startTime: string | null | undefined,
  endTime: string | null | undefined
): string | null => {
  const start = parseTimeSlot(startTime);
  const end = parseTimeSlot(endTime);
  if (start === null || end === null) return null;
  return `${formatHour(start)}–${formatHour(end)}`;
};

export const tileSegments = (
  segments: ParsedSegment[]
): Array<{ duration: number; fill: string }> => {
  const sorted = [...segments].sort((a, b) => a.startHour - b.startHour);
  const tiles: Array<{ duration: number; fill: string }> = [];
  let cursor = 0;
  for (const seg of sorted) {
    if (seg.startHour > cursor) {
      tiles.push({ duration: seg.startHour - cursor, fill: pieChartConfig.colors.gapFill });
      cursor = seg.startHour;
    }
    if (seg.endHour > cursor) {
      tiles.push({ duration: seg.endHour - cursor, fill: seg.fill });
      cursor = seg.endHour;
    }
  }
  if (cursor < TOTAL_HOURS) {
    tiles.push({ duration: TOTAL_HOURS - cursor, fill: pieChartConfig.colors.gapFill });
  }
  return tiles;
};
